/**
 * Parse pathKey into segments
 * @param {string} pathKey - Full pathKey string
 * @returns {string[]} Array of segments
 */
export function parsePathKey(pathKey) {
  if (!pathKey) return [];
  return pathKey.split("__").filter(Boolean);
}

/**
 * Extract raw ID from prefixed segment
 * @param {string} segment - Single segment (e.g., "product_abc", "mg_123", "op_xyz")
 * @returns {string} Raw ID without prefix
 */
export function extractIdFromSegment(segment) {
  if (segment.startsWith("product_")) return segment.slice(8);
  if (segment.startsWith("op_")) return segment.slice(3);
  if (segment.startsWith("mg_")) return segment.slice(3);
  return segment;
}

/**
 * Get nesting level from pathKey
 * Level = count("mg_") - 1
 * @param {string} pathKey - Full pathKey
 * @returns {number} Nesting level (0, 1, 2, ...)
 */
export function getPathKeyLevel(pathKey) {
  if (!pathKey) return 0;
  const mgCount = (pathKey.match(/mg_/g) || []).length;
  return Math.max(0, mgCount - 1);
}

/**
 * Extract parent option's pathKey (for navigation up)
 * @param {string} pathKey - Current modifier group's pathKey
 * @returns {string|null} Parent option's pathKey or null if at product level
 */
export function extractParentOptionPathKey(pathKey) {
  if (!pathKey) return null;
  const parts = parsePathKey(pathKey);
  const opIndices = [];

  for (let i = 0; i < parts.length; i++) {
    if (parts[i].startsWith("op_")) {
      opIndices.push(i);
    }
  }

  if (opIndices.length <= 1) {
    return null; // At product level
  }

  const parentOpIndex = opIndices[opIndices.length - 2];
  return parts.slice(0, parentOpIndex + 1).join("__");
}

/**
 * Compute a visual depth from a full pathKey based on total segments.
 * Example:
 *  - "product_pizza__mg_toppings" -> depth 0
 *  - "product_pizza__mg_toppings__op_pepperoni" -> depth 1
 *  - "product_pizza__mg_toppings__op_pepperoni__mg_amount" -> depth 2
 *  - "product_pizza__mg_toppings__op_pepperoni__mg_amount__op_extra" -> depth 3
 */
export function getPathVisualDepth(pathKey) {
  if (!pathKey) return 0;
  const parts = parsePathKey(pathKey);
  // First two segments are "product_x" and first "mg_x" – treat that as depth 0
  return Math.max(0, parts.length - 2);
}

/**
 * Build hierarchical tree from flat modifier groups array
 * @param {Array} modifierGroups - Flat array of modifier groups
 * @returns {Array} Hierarchical tree structure
 */
export function buildModifierTree(modifierGroups) {
  if (
    !modifierGroups ||
    !Array.isArray(modifierGroups) ||
    modifierGroups.length === 0
  ) {
    return [];
  }

  // Create a map of modifier groups by pathKey
  const groupsByPathKey = {};
  const allGroups = [];

  modifierGroups.forEach((mg) => {
    const pathKey = mg.pathKey || `mg_${mg._id}`;

    const groupNode = {
      ...mg,
      pathKey,
      level: getPathKeyLevel(pathKey),
      nestedGroups: [],
    };

    groupsByPathKey[pathKey] = groupNode;
    allGroups.push(groupNode);
  });

  // Build tree structure - find root groups (level 0)
  const rootGroups = allGroups.filter((g) => g.level === 0);

  // For each group, find and attach nested groups
  allGroups.forEach((group) => {
    if (!group.modifierOptions) return;

    // Convert modifierOptions to array if needed
    const options = Array.isArray(group.modifierOptions)
      ? group.modifierOptions
      : Object.values(group.modifierOptions);

    options.forEach((option) => {
      const optionId = option._id || option.id;
      if (!optionId) return;

      const optionPathKey = `${group.pathKey}__op_${optionId}`;

      // Find all groups that are DIRECT children of this option
      // They should start with: optionPathKey + '__mg_' and have exactly one extra segment
      const nestedGroups = allGroups.filter((nestedGroup) => {
        if (nestedGroup.pathKey === group.pathKey) return false; // Don't include self
        if (nestedGroup.level <= group.level) return false; // Must be deeper level

        const expectedPrefix = `${optionPathKey}__mg_`;
        if (!nestedGroup.pathKey.startsWith(expectedPrefix)) return false;

        // Ensure this is a direct child, not a grand‑child (match segment length rule from NESTED_PATH_LOGIC)
        const childSegments = nestedGroup.pathKey.split("__").length;
        const parentSegments = optionPathKey.split("__").length;
        return childSegments === parentSegments + 1;
      });

      // Attach nested groups to the option (we'll use this in rendering)
      if (nestedGroups.length > 0) {
        option.nestedGroups = nestedGroups;
      }
    });
  });

  return rootGroups;
}

/**
 * Check if pathKey contains a specific option ID
 * @param {string} pathKey - Full pathKey
 * @param {string} optionId - Option ID to search for
 * @returns {boolean} True if found
 */
export function pathKeyContainsOptionId(pathKey, optionId) {
  if (!pathKey || !optionId) return false;
  const parts = parsePathKey(pathKey);
  const optionSegment = `op_${optionId}`;
  return parts.some((part) => part === optionSegment);
}

/**
 * Extract parent option ID from a modifier group's pathKey
 * Used to find the parent option that this modifier group belongs to
 * @param {string} pathKey - Modifier group's pathKey
 * @returns {string|null} Parent option ID or null if at product level
 */
export function extractParentOptionId(pathKey) {
  if (!pathKey) return null;
  const parts = parsePathKey(pathKey);
  const opIndices = [];

  for (let i = 0; i < parts.length; i++) {
    if (parts[i].startsWith("op_")) {
      opIndices.push(i);
    }
  }

  if (opIndices.length === 0) {
    return null; // No options in path, at product level
  }

  // Get the last option ID before the last mg_ segment
  const lastMgIndex = pathKey.lastIndexOf("__mg_");
  if (lastMgIndex > 0) {
    const beforeLastMg = pathKey.substring(0, lastMgIndex);
    const partsBeforeLastMg = parsePathKey(beforeLastMg);
    for (let i = partsBeforeLastMg.length - 1; i >= 0; i--) {
      if (partsBeforeLastMg[i].startsWith("op_")) {
        return extractIdFromSegment(partsBeforeLastMg[i]);
      }
    }
  }

  return null;
}
